import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, forkJoin, map, Observable, of } from 'rxjs';
import { IdObject } from '@campaign-portal/namespace/common/idObject';
import { exist, Id, UUID } from '@campaign-portal/namespace/common/id';
import { Channel } from '@campaign-portal/namespace/entities/channels/specs';
import { ContactGroup } from '@campaign-portal/namespace/entities/contact-groups/specs';
import { SenderSubscription } from '@campaign-portal/namespace/entities/sender-id/specs';
import { MessageTemplate } from '@campaign-portal/namespace/entities/templates/specs';
import { LinkDomain } from '@campaign-portal/namespace/entities/link-domains/specs';
import {
	ChannelsService,
	ContactFieldsService,
	ContactGroupsService,
	CountriesNetworksService,
	LinkDomainsService,
	MessageTemplatesService,
	SendersService,
} from '../services';
import { AlarisAccountSettingsService } from '@campaign-portal/components-library';

export enum RepoType {
	CHANNELS = 'ch',
	GROUPS = 'cGroup',
	DELETED_GROUPS = 'cDeletedGroup',
	SENDERS = 'sender',
	TEMPLATE = 'template',
	LINK_DOMAIN = 'linkDomains',
}

@Injectable({
	providedIn: 'root',
})
export class RepoService {
	public readonly loaded$ = new BehaviorSubject<boolean>(false);

	constructor(
		// with pipes:
		private readonly channelsService: ChannelsService,
		private readonly contactGroupsService: ContactGroupsService,
		private readonly sendersService: SendersService,
		private readonly templatesService: MessageTemplatesService,
		private readonly domainsService: LinkDomainsService,
		// just stores
		private readonly countriesNetworksService: CountriesNetworksService,
		private readonly contactFieldsService: ContactFieldsService,
		private readonly accountSettings: AlarisAccountSettingsService
	) {}

	public transform(id: Id<exist> | IdObject<exist>, type: RepoType.CHANNELS): Channel | null;
	public transform(
		id: Id<exist> | IdObject<exist>,
		type: RepoType.GROUPS | RepoType.DELETED_GROUPS
	): ContactGroup | null;
	public transform(id: Id<exist> | IdObject<exist>, type: RepoType.SENDERS): SenderSubscription | null;
	public transform(id: Id<exist> | IdObject<exist>, type: RepoType.TEMPLATE): MessageTemplate | null;
	public transform(id: Id<exist> | IdObject<exist>, type: RepoType.LINK_DOMAIN): LinkDomain | null;
	public transform(id: Id<exist> | IdObject<exist>, type: RepoType): IdObject | null {
		switch (type) {
			case RepoType.CHANNELS:
				return this.findEntity<Channel>(id, this.channelsService.map);
			case RepoType.GROUPS:
				return this.findEntity<ContactGroup>(id, this.contactGroupsService.map);
			case RepoType.DELETED_GROUPS:
				return this.findEntity<ContactGroup>(id, this.contactGroupsService.removedMap$.getValue());
			case RepoType.SENDERS:
				return this.findEntity<SenderSubscription>(id, this.sendersService.map);
			case RepoType.TEMPLATE:
				return this.findEntity<MessageTemplate>(id, this.templatesService.map);
			case RepoType.LINK_DOMAIN:
				return this.findEntity<LinkDomain>(id, this.domainsService.map);
			default:
				return null;
		}
	}

	public reset(): void {
		this.channelsService.clear();

		this.contactGroupsService.clear();

		this.contactGroupsService.removedMap$.next(new Map());

		this.sendersService.clear();

		this.templatesService.clear();

		this.countriesNetworksService.clear();

		this.contactFieldsService.clear();

		this.domainsService.clear();

		this.loaded$.next(false);
	}

	// eslint-disable-next-line max-lines-per-function
	public load(): Observable<unknown> {
		const repositoriesRequests: Observable<unknown>[] = [
			this.accountSettings.load(),
			this.channelsService.load().pipe(
				catchError((resp) => {
					resp.Data = [];
					return of(resp);
				})
			),
			this.countriesNetworksService.load().pipe(
				catchError((resp) => {
					resp.Data = [];
					return of(resp);
				})
			),
			this.contactFieldsService.load().pipe(
				catchError((resp) => {
					resp.Data = [];
					return of(resp);
				})
			),
			this.contactGroupsService.load().pipe(
				catchError((resp) => {
					resp.Data = [];
					return of(resp);
				})
			),
			this.contactGroupsService.loadRemoved().pipe(
				catchError((resp) => {
					resp.Data = [];
					return of(resp);
				})
			),
			this.sendersService.load().pipe(
				catchError((resp) => {
					resp.Data = [];
					return of(resp);
				})
			),
			this.templatesService.load().pipe(
				catchError((resp) => {
					resp.Data = [];
					return of(resp);
				})
			),
			this.domainsService.load().pipe(
				catchError((resp) => {
					resp.Data = [];
					return of(resp);
				})
			),
		];

		return forkJoin(repositoriesRequests).pipe(
			map((resp) => {
				this.loaded$.next(true);
				return resp;
			})
		);
	}

	private findEntity<T>(ids: Id<exist> | IdObject<exist>, dataMap: Map<exist | UUID, T>): T | null {
		if (typeof ids === 'number') {
			const found = dataMap.get(ids);
			return found ?? null;
		} else if (typeof ids === 'object' && !Array.isArray(ids)) {
			const found = dataMap.get(ids.id);
			return found ?? null;
		}
		return null;
	}
}
